import { BrightcoveVideo } from "@blocks/BrightcoveVideo/BrightcoveVideo";
import CASBlock from "@blocks/CASBlock/CASBlock";
import ArticleImage from "@components/Layouts/Carousel/ArticleImage";
import TrendingArticleCarousel from "@components/Layouts/Carousel/TrendingArticleCarousel";
import { AdsImu1, AdsMidArticle } from "@elements/Advertisement/variant/AdsImu";
import {
  CompanionAdsImu1,
  CompanionAdsMidArticle,
} from "@elements/Advertisement/variant/CompanionAdImu";
import ApplyParagraphAnnotations from "@elements/ArticleParagraph/ApplyParagraphAnnotations";
import Paywall from "@elements/ArticleParagraph/ArticlePaywall";
import Regwall, {
  ArticleGaEventsDataProps,
} from "@elements/ArticleParagraph/ArticleRegwall";
import { YOUTUBE_WATCH_URL } from "@elements/ArticleParagraph/Embeds/constants";
import Embed from "@elements/ArticleParagraph/Embeds/Embed";
import YouTubeEmbedCustom from "@elements/ArticleParagraph/Embeds/YouTubeEmbedCustom";
import GetLimitedParagraphsFromArticleBody from "@elements/ArticleParagraph/GetLimitedParagraphsFromArticleBody";
import ParagraphList from "@elements/ArticleParagraph/ParagraphElements/ParagraphList";
import PullQuoteCard from "@elements/ArticleParagraph/ParagraphElements/PullQuoteCard";
import SubHead from "@elements/ArticleParagraph/ParagraphElements/SubHead";
import WrapperContainer from "@elements/ArticleParagraph/ParagraphElements/WrapperContainer";
import Paragraph from "@elements/Typography/Paragraph";
import debounce from "@helper/debounce";
import {
  CAS_CAMPAIGN_ID,
  SHOW_PAYWALL,
  SHOW_REGWALL,
} from "@helper/getEnvVariables";
import setArticleVisitCookie, {
  SphUserArticleCountCookie,
} from "@helper/setArticleVisitCookie";
import { ProcessedArticleData } from "@helper/transformer/useOsResponse";
import { formatDate } from "@helper/useDateFormat";
import { getWindowOrigin, isClientSide } from "@helper/utils";
import { isElementInViewPort } from "@helper/visibilityHelpers";
import { useTMCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTMCustomEventAnalyticsCall";
import useCasScript from "@hooks/useCasScript";
import { getUserType, OKTAUserTypeEnum } from "@hooks/useUserInfo";
import {
  ArticleAdPageTargetingType,
  ArticleAdSlotTargetingType,
  PageAdTargetingTypeEnum,
} from "@typings/Ads.d";
import {
  type ProcessedArticleBody,
  ProcessedAuthorContent,
  ProcessedTagsContent,
} from "@typings/Opensearch.d";
import Cookies from "js-cookie";
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { twMerge } from "tailwind-merge";

import InfiniteScrollReadMoreCTA from "../Buttons/InfiniteScrollReadMoreCTA";

export type ArticleParagraphProps = {
  /**
   * ID of the article.
   */
  articleId: string;

  /**
   * Article title.
   */
  articleTitle: string;

  /**
   * Processed content of the article body.
   */
  articleBody: ProcessedArticleBody;

  /**
   * Section name related to article.
   */
  sectionName: string | undefined;

  /**
   * Assigned tags to article.
   */
  tags?: ProcessedTagsContent[];

  /**
   * Article page URL.
   */
  pageUrl: string;

  /**
   * Infinite scrolled page number of the article.
   */
  pageNumber: number;

  /**
   * Article created date.
   */
  dateCreated: string;

  /**
   * Controls whether the article uses infinite scrolling.
   */
  isInfiniteScrollArticle?: boolean;

  /**
   * Authors related with the article.
   */
  authors?: ProcessedAuthorContent[];

  /**
   * Article-level ad targeting values.
   */
  articlePageTarget?: ArticleAdPageTargetingType[];

  /**
   * Article ad slot targeting values.
   */
  articleSlotTarget?: ArticleAdSlotTargetingType[];

  /**
   * Google ad channels used for ad targeting.
   */
  gsChannels?: string[];

  /**
   * Optional endnote displayed at the end of the article.
   */
  endnote?: string;

  /**
   * Controls whether the article is displayed as a CUE preview.
   */
  isCuePreview?: boolean;

  /**
   * Articles displayed in the trending articles section.
   * */
  trendingArticles?: ProcessedArticleData[];

  /**
   * Total number of paragraphs in the article body.
   */
  bodyParagraphTotal?: number;

  /**
   * Checks whether the full article content is expanded.
   */
  isArticleExpanded?: boolean;

  /**
   * Updates the expanded state of the article.
   */
  setIsArticleExpanded?: Dispatch<SetStateAction<boolean>>;

  /**
   * Checks whether the article first-fold design is enabled.
   * @default false
   */
  isArticleFirstFoldEnabled?: boolean;
};

/**
 * Renders the paragraphs and content elements of an article.
 */
export default function ArticleParagraph({
  articleId,
  articleTitle,
  articleBody,
  sectionName,
  tags,
  pageUrl,
  pageNumber,
  dateCreated,
  isInfiniteScrollArticle,
  authors,
  articlePageTarget,
  articleSlotTarget,
  gsChannels,
  endnote,
  isCuePreview = false,
  trendingArticles = [],
  bodyParagraphTotal = 0,
  isArticleExpanded = true,
  setIsArticleExpanded,
  isArticleFirstFoldEnabled = false,
}: ArticleParagraphProps) {
  const origin = getWindowOrigin();

  const tmCustomGaEventsProps: ArticleGaEventsDataProps = useMemo(() => {
    return {
      articleid: articleId,
      contenttype: "1",
      chapter1: "",
      level2: sectionName,
      pubdate: formatDate(dateCreated, "yyyy-MM-dd HH:mm:ss"),
      title: articleTitle,
      url: origin + pageUrl,
      keyword: tags ? tags.map((tags) => tags.name).join(", ") : "",
      byline: authors ? authors.map((author) => author.name).join(", ") : "",
    };
  }, [
    articleId,
    articleTitle,
    sectionName,
    dateCreated,
    pageUrl,
    tags,
    authors,
    origin,
  ]);

  const TmCustomGaEvent = useTMCustomEventAnalyticsCall();
  const [enableRegwall, setEnableRegwall] = useState<boolean>(false);
  const [enablePaywall, setEnablePaywall] = useState<boolean>(false);
  const [cookieData, setCookieData] =
    useState<SphUserArticleCountCookie | null>(null);
  const { isScriptReady: isCasScriptReady } = useCasScript();

  useEffect(() => {
    if (!isClientSide) return;

    const cookieName = "sph_user_article_count";
    const existingCookie = Cookies.get(cookieName);

    if (existingCookie) {
      try {
        const parsed = JSON.parse(existingCookie) as SphUserArticleCountCookie;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setCookieData(parsed);
      } catch {
        setCookieData(null);
      }
    } else {
      setCookieData(null);
    }
  }, []);

  useEffect(() => {
    if (!cookieData) return;

    const userType = getUserType();

    let visitCount = 0;
    let UserArticleVisitNumber = visitCount;

    visitCount = cookieData.count;
    UserArticleVisitNumber = visitCount;
    // Get the visit count
    if (cookieData.nids.includes(articleId)) {
      UserArticleVisitNumber = cookieData.nids.indexOf(articleId) + 1;
    }

    if (
      !isCuePreview &&
      parseInt(SHOW_REGWALL) &&
      visitCount >= 5 &&
      UserArticleVisitNumber >= 5 &&
      (userType === OKTAUserTypeEnum.ANONYMOUS || userType === undefined)
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setEnableRegwall(true);
    }
    if (
      !isCuePreview &&
      parseInt(SHOW_REGWALL) &&
      parseInt(SHOW_PAYWALL) &&
      visitCount >= 7 &&
      UserArticleVisitNumber >= 7 &&
      userType !== OKTAUserTypeEnum.SUBSCRIBER &&
      userType === OKTAUserTypeEnum.REGISTERED
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setEnableRegwall(false);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setEnablePaywall(true);
    }
  }, [articleId, cookieData, isCuePreview]);

  const handleScroll = useCallback(() => {
    if (!isClientSide) return;
    const articleBody = document.querySelectorAll(
      `.article-body-wrapper-${articleId}`,
    );
    articleBody.forEach((article) => {
      if (isElementInViewPort(article, 0)) {
        // Set user page visit count and ids.
        setArticleVisitCookie({ articleId });

        // Retrieve the existing cookie
        const cookieName = "sph_user_article_count";
        const existingCookie = Cookies.get(cookieName);
        if (existingCookie) {
          const cookieDataParsed = JSON.parse(
            existingCookie,
          ) as SphUserArticleCountCookie;
          setCookieData(cookieDataParsed);
        }
      }
    });
  }, [articleId]);

  useEffect(() => {
    if (!isClientSide) return;
    const debouncedHandleScroll = debounce(handleScroll, 100);
    window.addEventListener("scroll", debouncedHandleScroll);
    return () => {
      window.removeEventListener("scroll", debouncedHandleScroll);
    };
  }, [handleScroll]);

  const articleParagraphs = useMemo(() => {
    if (enableRegwall || enablePaywall) {
      return GetLimitedParagraphsFromArticleBody(articleBody, 1);
    }

    if (!isArticleExpanded) {
      return GetLimitedParagraphsFromArticleBody(articleBody, 3);
    }

    return articleBody;
  }, [articleBody, enableRegwall, enablePaywall, isArticleExpanded]);

  useEffect(() => {
    const cookieName = "sph_user_article_count";
    const existingCookie = Cookies.get(cookieName);
    if (enableRegwall) {
      // Set GA events.
      TmCustomGaEvent({
        event: "customEvent",
        eventCategory: "Article Hard Regwall",
        eventAction: "Visible",
        abVariant: "hard regwall",
        eventLabel: existingCookie,
        ...tmCustomGaEventsProps,
      });
    }
    if (enablePaywall) {
      // Set GA events.
      TmCustomGaEvent({
        event: "customEvent",
        eventCategory: "Article Paywall",
        eventAction: "Visible",
        abVariant: "article paywall",
        eventLabel: existingCookie,
        ...tmCustomGaEventsProps,
      });
    }
  }, [TmCustomGaEvent, tmCustomGaEventsProps, enableRegwall, enablePaywall]);

  let paragraphCount = 0;
  const pageAdTarget = PageAdTargetingTypeEnum.ARTICLE;

  let adsImu1 = (
    <div
      className={twMerge(
        !isArticleFirstFoldEnabled &&
          "border-y border-solid border-gray-2200 mb-5",
      )}
    >
      <AdsImu1
        pageAdTargetType={pageAdTarget}
        sectionName={sectionName}
        rootClassName={"pt-5"}
        gsChannels={gsChannels}
        articlePageTarget={articlePageTarget}
      />
    </div>
  );
  let adsMidArticle = (
    <div
      className={twMerge(
        !isArticleFirstFoldEnabled &&
          "border-y border-solid border-gray-2200 mb-5",
      )}
    >
      <AdsMidArticle
        pageAdTargetType={pageAdTarget}
        sectionName={sectionName}
        rootClassName={"py-5"}
        gsChannels={gsChannels}
        articlePageTarget={articlePageTarget}
      />
    </div>
  );

  if (isInfiniteScrollArticle) {
    adsImu1 = (
      <div
        className={twMerge(
          !isArticleFirstFoldEnabled &&
            "border-y border-solid border-gray-2200 mb-5",
        )}
      >
        <CompanionAdsImu1
          uniqueSectionName={sectionName}
          pageAdTargetType={pageAdTarget}
          pageNumber={pageNumber}
          articlePageTarget={articlePageTarget}
          articleSlotTarget={articleSlotTarget}
          rootClassName={"pt-5"}
          gsChannels={gsChannels}
        />
      </div>
    );
    adsMidArticle = (
      <div
        className={twMerge(
          !isArticleFirstFoldEnabled &&
            "border-y border-solid border-gray-2200 mb-5",
        )}
      >
        <CompanionAdsMidArticle
          uniqueSectionName={sectionName}
          pageAdTargetType={pageAdTarget}
          pageNumber={pageNumber}
          articlePageTarget={articlePageTarget}
          articleSlotTarget={articleSlotTarget}
          rootClassName={"py-5"}
          gsChannels={gsChannels}
        />
      </div>
    );
  }

  return (
    <div
      data-testid="article-body"
      className={`article-body-wrapper-${articleId}`}
    >
      {articleParagraphs.map((element, index) => {
        let bannersAndAds = null;
        let trendingSection = null;
        if (paragraphCount === 0) {
          if (enableRegwall) {
            bannersAndAds = (
              <>
                <Regwall articleData={tmCustomGaEventsProps} />
                {adsImu1}
              </>
            );
          } else if (enablePaywall) {
            bannersAndAds = (
              <>
                <Paywall articleData={tmCustomGaEventsProps} />
                {adsImu1}
              </>
            );
          } else {
            bannersAndAds = adsImu1;
          }
        } else if (paragraphCount === 2) {
          if (!isArticleExpanded) {
            bannersAndAds = (
              <div className="w-full flex flex-col items-center gap-5 -mt-9 mb-5">
                <div className="w-full h-[30px] bg-gradient-to-t from-white-100 to-white-100/0"></div>
                <InfiniteScrollReadMoreCTA
                  onClick={() => {
                    setIsArticleExpanded?.(true);
                    TmCustomGaEvent({
                      event: "customEvent",
                      eventCategory: "continue reading",
                      eventAction: "click",
                      eventLabel: origin + pageUrl,
                      ...tmCustomGaEventsProps,
                    });
                  }}
                />
              </div>
            );
          }
        } else if (paragraphCount === 5) {
          bannersAndAds = adsMidArticle;
        } else if (
          bodyParagraphTotal >= 9 &&
          paragraphCount === 7 &&
          trendingArticles.length > 0
        ) {
          trendingSection = (
            <TrendingArticleCarousel
              trendingArticles={trendingArticles}
              rootClassName={twMerge(
                "track-impression mt-6 overflow-hidden",
                !isArticleFirstFoldEnabled &&
                  "border-y border-solid border-gray-2200",
              )}
              testId="trending-article-carousel"
              dataSectionName="trending-story-middle"
              source="trending-story-middle"
            />
          );
        }

        switch (element.type) {
          case "paragraph":
            paragraphCount++;
            return (
              <Fragment key={index}>
                <Paragraph
                  testid={`paragraph-${index}`}
                  id={element.id}
                  className={twMerge(
                    !isArticleExpanded && paragraphCount === 3
                      ? "mb-0 text-gray-800"
                      : paragraphCount === 1
                        ? "article-summary"
                        : "",
                    isArticleFirstFoldEnabled && "mb-5",
                  )}
                >
                  {ApplyParagraphAnnotations(
                    element.value,
                    element.annotations,
                  )}
                </Paragraph>
                {bannersAndAds}
                {trendingSection}
              </Fragment>
            );

          case "sub_head":
            return (
              <Fragment key={index}>
                <SubHead element={element} />
              </Fragment>
            );

          case "image": {
            const imageHeight =
              element.image?.height && element.image.height >= 1460
                ? 1460
                : 550;
            return (
              <ArticleImage
                key={index}
                url={element.image?.url}
                alt={element.caption}
                caption={element.caption}
                credit={element.credit}
                height={imageHeight}
                className="mb-10 mt-2"
                isOriginalCrop={element.cropSelection === "original"}
              />
            );
          }

          case "embed":
            return (
              <Fragment key={index}>
                <Embed className="my-lg" src={element.embedUrl} />
              </Fragment>
            );

          case "pull_quote":
            return (
              <Fragment key={index}>
                <PullQuoteCard
                  quote={element.quote}
                  quotee={element.quotee}
                  quoteLabel={element.quoteLabel}
                  quoteCredit={element.quoteCredit}
                />
              </Fragment>
            );

          case "list_bulleted":
          case "list_numbered": {
            const listType = element.type === "list_bulleted" ? "ol" : "ul";
            const listItems = element.listItem;
            const listStyle =
              element.listStyle === "decimal" ? "list-decimal" : "list-disc";
            return (
              <Fragment key={index}>
                <ParagraphList
                  listStyle={listStyle}
                  listType={listType}
                  listItems={listItems}
                />
              </Fragment>
            );
          }

          case "youtube_video": {
            const url = `${YOUTUBE_WATCH_URL}${element.youtubeId}`;
            return (
              <Fragment key={index}>
                <YouTubeEmbedCustom url={url} className="my-lg" />
              </Fragment>
            );
          }

          case "brightcove_video": {
            return (
              <Fragment key={index}>
                <WrapperContainer className="block my-lg">
                  <BrightcoveVideo videoId={element.videoId} />
                </WrapperContainer>
              </Fragment>
            );
          }

          default:
            return null;
        }
      })}

      {isArticleExpanded && (
        <>
          {endnote && (
            <div className="bg-gray-600 px-2.5 py-0.5 mb-4">{endnote}</div>
          )}

          {isCasScriptReady && (
            <CASBlock casId={CAS_CAMPAIGN_ID} index={pageNumber} />
          )}
        </>
      )}
    </div>
  );
}
